import { Vms, VmsEnrichment, VmsFault, VmsStatus } from '_lib/api';
import { Ivim } from '_store/ivim/ivimsTypes';

import { isVmsEnrolled, vmsDisplayName, VmsStatusAlert } from './pmvsTypes';

/** Matches RSU map marker green (see `greenRsu.png` / event progress color). */
const PMV_ICON_GREEN = '#4fca4f';
/** Matches Material red used for RSU down / error. */
const PMV_ICON_RED = '#f44336';

const pmvMapIconSvg = (color: string): string => `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <rect x="1.5" y="1.5" width="29" height="29" rx="3" fill="#ffffff" stroke="${color}" stroke-width="3"/>
  <rect x="7" y="10" width="18" height="12" rx="3" fill="${color}"/>
  <rect x="9" y="13" width="6" height="2" rx="0.5" fill="#ffffff"/>
  <rect x="9" y="17" width="11" height="2" rx="0.5" fill="#ffffff"/>
</svg>`;

/** Green when working/legacy; red when `working === false`. Defaults to green. */
export const pmvStatusIcon = (working = true): string => {
  const color = working ? PMV_ICON_GREEN : PMV_ICON_RED;
  return `data:image/svg+xml,${encodeURIComponent(pmvMapIconSvg(color))}`;
};

/** Legacy (`status == null`) or explicit `working === true` → operational. */
export const isVmsWorking = (vms: Vms): boolean =>
  vms.status == null || vms.status.working === true;

/** Notify when a known enrolled VMS flips between operational and not working. */
export const collectVmsWorkingStatusAlerts = (
  previous: Vms[],
  next: Vms[],
  now = Date.now(),
): VmsStatusAlert[] => {
  const prevById = new Map(previous.map((vms) => [vms.id, vms]));
  const alerts: VmsStatusAlert[] = [];

  for (const vms of next) {
    if (!isVmsEnrolled(vms)) continue;

    const prev = prevById.get(vms.id);
    if (!prev) continue;

    const wasWorking = isVmsWorking(prev);
    const isWorking = isVmsWorking(vms);
    if (wasWorking === isWorking) continue;

    const createdAt = new Date(now).toISOString();
    alerts.push({
      id: `${vms.id}-${now}`,
      vmsId: vms.id,
      kind: isWorking ? 'recovered' : 'fault',
      name: vmsDisplayName(vms) || vms.id,
      createdAt,
    });
  }

  return alerts;
};

type RawVmsItem = Vms & { enrollment?: VmsEnrichment; status?: VmsStatus | null };

const normalizeFault = (raw: unknown): VmsFault | null => {
  if (!raw || typeof raw !== 'object') return null;
  const fault = raw as Partial<VmsFault>;
  if (typeof fault.code !== 'string' || !fault.code) return null;
  const scope = fault.scope === 'unit' || fault.scope === 'vms' ? fault.scope : 'vms';
  return {
    scope,
    code: fault.code,
    description: typeof fault.description === 'string' ? fault.description : undefined,
    severity: typeof fault.severity === 'string' ? fault.severity : undefined,
    lastUpdateTime: typeof fault.lastUpdateTime === 'string' ? fault.lastUpdateTime : undefined,
  };
};

const normalizeStatus = (raw: unknown): VmsStatus | null => {
  if (raw == null || typeof raw !== 'object') return null;
  const status = raw as Partial<VmsStatus> & { faults?: unknown };
  const faults = Array.isArray(status.faults)
    ? status.faults.map(normalizeFault).filter((f): f is VmsFault => f != null)
    : [];
  return {
    working: status.working === true,
    faults,
    timeLastSet: typeof status.timeLastSet === 'string' ? status.timeLastSet : null,
    updatedAt: typeof status.updatedAt === 'string' ? status.updatedAt : '',
  };
};

const normalizeEnrichment = (raw: RawVmsItem): VmsEnrichment | undefined => {
  const source = raw.enrichment ?? raw.enrollment;
  if (!source) return undefined;

  return {
    name: String(source.name ?? '').trim(),
    rsus: source.rsus ?? [],
    enrolled: source.enrolled === true,
  };
};

export const normalizeVmsList = (response: unknown): Vms[] => {
  if (!Array.isArray(response)) {
    return [];
  }

  return response.map((item) => {
    const raw = item as RawVmsItem;
    const enrichment = normalizeEnrichment(raw);

    return {
      id: String(raw.id ?? ''),
      roadNumber: raw.roadNumber ?? '',
      roadDirection: raw.roadDirection ?? '',
      latitude: raw.latitude ?? 0,
      longitude: raw.longitude ?? 0,
      description: raw.description ?? '',
      type: raw.type ?? '',
      mounting: raw.mounting ?? '',
      enrichment,
      status: normalizeStatus(raw.status),
    };
  });
};

/** GET /vms/{id}/ivim may return a single IVIM, an array, or a paginated `{ results }` payload. */
export const normalizeVmsIvimResponse = (response: unknown): Ivim[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response as Ivim[];

  if (typeof response === 'object') {
    const payload = response as { results?: unknown; id?: unknown };
    if (Array.isArray(payload.results)) return payload.results as Ivim[];
    if (typeof payload.id === 'string') return [response as Ivim];
  }

  return [];
};

export const vmsSelectLabel = (vms: Vms): string =>
  `${vms.id} - ${vms.roadNumber} ${vms.description}`.trim();

export const isVmsEnrolledOption = (vms: Vms): boolean => vms.enrichment?.enrolled === true;

type RsuLookup = { name: string; stationId?: string; id?: string };

/** Map API/form RSU ref (name or legacy stationId) to the RSU display name sent to VMS API. */
export const resolveRsuName = (ref: string, devices: RsuLookup[]): string =>
  devices.find((device) => device.name === ref)?.name ??
  devices.find((device) => device.stationId === ref)?.name ??
  ref;

export const resolveRsuNames = (refs: string[], devices: RsuLookup[]): string[] =>
  refs.map((ref) => resolveRsuName(ref, devices));

export const findDeviceByRsuRef = (ref: string, devices: RsuLookup[]): RsuLookup | undefined =>
  devices.find((device) => device.name === ref) ??
  devices.find((device) => device.stationId === ref);
